/**
 * Unit models, batch 2 (ADR-0075): medieval and early-modern land units, the first
 * ships, and the unique units that replace them.
 */

import * as T from 'three';
import { COL, kit, mat, person, shadeOf, type Kit, type V3 } from './parts.js';
import { mounted } from './units-ancient.js';
import type { ModelBuilder } from './units.js';

const DARK_SKIN = 0x6e4630;

/** A wooden hull along x: keel line, sheer, planking, and a rail. Deck at y = deck. */
export function hull(k: Kit, length: number, beam: number, deck: number, colour: number, bowRise = 1.6, sternRise = 1.2): void {
  const h = length / 2;
  const profile: Array<[number, number]> = [
    [-h + 1.2, 0.6],
    [h - 2.2, 0.6],
    [h + 0.6, deck + bowRise],
    [h - 0.4, deck + bowRise],
    [h - 1.6, deck],
    [-h + 1.0, deck],
    [-h, deck + sternRise],
    [-h - 0.6, deck + sternRise],
    [-h, deck * 0.5],
  ];
  k.shape(profile, beam, colour, 0, 0, -beam / 2);
  for (let y = 1.4; y < deck; y += 1.1) for (const z of [beam / 2 + 0.02, -beam / 2 - 0.02]) k.box(length - 2.6, 0.1, 0.04, -0.4, y, z, shadeOf(colour, 0.78));
  k.box(length - 2.2, 0.35, beam + 0.15, -0.6, deck, 0, shadeOf(colour, 0.8));
}

/** Oars along both sides, from the rail down into the water. */
function oars(k: Kit, from: number, to: number, count: number, beam: number, deck: number, y = deck - 0.4): void {
  for (let i = 0; i < count; i++) {
    const x = from + ((to - from) * i) / Math.max(1, count - 1);
    for (const z of [1, -1]) k.bar([x, y, (z * beam) / 2], [x - 0.8, 0.0, z * (beam / 2 + 3.4)], 0.07, 0.07, COL.WOOD, 4);
  }
}

/** A square sail on a yard, bellied slightly, with stripes in the owner's colour. */
function squareSail(g: T.Group, k: Kit, civ: number, x: number, top: number, width: number, height: number, stripes: boolean, cross: boolean): void {
  k.bar([x, top + 0.2, -width / 2 - 0.3], [x, top + 0.2, width / 2 + 0.3], 0.13, 0.13, COL.WOODD, 5);
  const sail = new T.Mesh(new T.CylinderGeometry(width * 1.4, width * 1.4, height, 10, 1, true, -0.38, 0.76), mat(COL.WHITE, true));
  sail.rotation.y = Math.PI / 2;
  sail.position.set(x - width * 1.4 + 0.6, top - height / 2, 0);
  g.add(sail);
  if (stripes) for (const dz of [-width / 4, width / 4]) k.box(0.06, height, width / 6, x + 0.62, top - height / 2, dz, civ);
  if (cross) {
    k.box(0.06, height * 0.7, 0.5, x + 0.64, top - height / 2, 0, civ);
    k.box(0.06, 0.5, width * 0.6, x + 0.64, top - height * 0.45, 0, civ);
  }
}

/** An elephant facing +x. */
function elephant(k: Kit, colour: number): void {
  for (const [x, z] of [[3.6, 1.6], [3.6, -1.6], [-3.4, 1.6], [-3.4, -1.6]] as Array<[number, number]>) {
    k.bar([x, 8.6, z], [x, 0.4, z], 1.15, 1.05, colour, 8);
    k.bar([x, 0.4, z], [x, 0, z], 1.2, 1.2, shadeOf(colour, 0.85), 8);
  }
  k.ball(4.6, 0, 10.6, 0, colour, 1, 1.45, 1.0, 0.85);
  k.ball(2.6, 6.2, 12.6, 0, colour, 1, 1.0, 1.05, 0.95);
  for (const z of [1, -1]) {
    k.ball(2.2, 5.2, 12.8, z * 2.2, shadeOf(colour, 0.92), 1, 0.35, 1.2, 1);
    k.bar([7.8, 10.6, z * 0.9], [9.6, 9.6, z * 1.1], 0.32, 0.18, COL.WHITE, 6);
    k.box(0.25, 0.3, 0.12, 8.2, 13.4, z * 1.4, COL.DARK);
  }
  k.bar([8.0, 11.6, 0], [9.2, 7.6, 0], 0.9, 0.7, colour, 8);
  k.bar([9.2, 7.6, 0], [9.0, 3.6, 0], 0.7, 0.5, colour, 8);
  k.bar([9.0, 3.6, 0], [9.8, 2.6, 0], 0.5, 0.4, colour, 8);
  k.bar([-6.4, 11.4, 0], [-7.2, 6.4, 0], 0.25, 0.15, colour, 5);
}

export const MEDIEVAL: Record<string, ModelBuilder> = {
  pikeman(g, civ) {
    const k = person(g, civ, {
      helmet: 'morion',
      cuirass: true,
      cuirassColour: COL.STEEL,
      legs: shadeOf(civ, 0.6),
      sleeve: shadeOf(civ, 0.85),
      lHand: [1.6, 10.4, 0.2],
      rHand: [0.8, 8.2, -1.0],
    });
    // A long pike lowered forward to receive a charge.
    k.bar([-6.0, 5.0, -0.6], [16.0, 13.4, 0.4], 0.13, 0.13, COL.WOOD, 6);
    k.cone([16.0, 13.4, 0.4], [17.6, 14.0, 0.45], 0.24, COL.STEEL, 4);
    k.box(0.12, 4.0, 0.2, 1.28, 9.8, 0.6, civ);
    k.box(0.12, 4.0, 0.2, 1.28, 9.8, -0.6, civ);
  },

  crossbowman(g, civ) {
    const k = person(g, civ, {
      helmet: 'morion',
      legs: shadeOf(civ, 0.55),
      lHand: [3.2, 11.0, 0.2],
      lElbow: [1.8, 10.2, 1.6],
      rHand: [1.0, 11.8, -0.6],
      rElbow: [-0.6, 10.8, -2.0],
    });
    // Quilted coat lines, the crossbow levelled at the shoulder, bolts at the hip.
    for (let y = 8.2; y < 11.4; y += 0.7) k.bar([0, y, 0], [0, y + 0.06, 0], 1.6, 1.6, shadeOf(civ, 0.8), 10).scale.z = 0.8;
    k.box(5.2, 0.45, 0.45, 2.4, 11.4, -0.2, COL.WOOD);
    k.bar([4.6, 11.5, -2.6], [4.6, 11.5, 2.2], 0.12, 0.12, COL.IRON, 5);
    k.bar([4.6, 11.5, -2.6], [1.0, 11.5, -0.2], 0.03, 0.03, COL.WHITE, 3);
    k.bar([4.6, 11.5, 2.2], [1.0, 11.5, -0.2], 0.03, 0.03, COL.WHITE, 3);
    k.box(0.9, 1.6, 0.6, -0.6, 7.2, -1.6, COL.LEATHER);
    for (const dz of [-0.15, 0.15]) k.box(0.1, 0.6, 0.1, -0.6, 8.3, -1.6 + dz, COL.WHITE);
    // A pavise on the back with the owner's arms.
    k.box(0.3, 6.4, 4.0, -2.0, 9.0, 0, shadeOf(civ, 0.9));
    k.box(0.06, 2.8, 0.7, -2.18, 9.4, 0, COL.WHITE);
    k.box(0.06, 0.7, 2.4, -2.18, 10.0, 0, COL.WHITE);
  },

  longbowman(g, civ) {
    const k = person(g, civ, {
      helmet: 'hood',
      legs: 0x6f7a4f,
      boots: COL.LEATHER,
      lHand: [3.4, 11.6, 1.0],
      lElbow: [1.7, 11.6, 1.8],
      rHand: [-0.4, 11.6, -0.4],
      rElbow: [-1.7, 11.2, -1.6],
    });
    const points: V3[] = [];
    for (let i = 0; i <= 12; i++) {
      const t = i / 12 - 0.5;
      points.push([3.7 - 1.0 * t * t * 4 * 0.45, 11.6 + t * 16.0, 1.0]);
    }
    for (let i = 0; i < 12; i++) k.bar(points[i]!, points[i + 1]!, 0.16, 0.16, i === 5 || i === 6 ? COL.LEATHER : 0xb08a52, 5);
    k.bar(points[0]!, [-0.4, 11.6, -0.2], 0.03, 0.03, COL.WHITE, 3);
    k.bar(points[12]!, [-0.4, 11.6, -0.2], 0.03, 0.03, COL.WHITE, 3);
    k.bar([-0.4, 11.6, -0.2], [5.4, 11.6, 0.9], 0.07, 0.07, COL.WOOD, 4);
    // Arrows stuck point-down in the ground before him, ready to hand.
    for (const [x, z] of [[4.4, -2.4], [5.0, -2.0], [4.7, -2.8]] as Array<[number, number]>) {
      k.bar([x, 0, z], [x - 0.3, 4.2, z + 0.1], 0.06, 0.06, COL.WOOD, 4);
      k.box(0.08, 0.7, 0.3, x - 0.3, 4.3, z + 0.1, civ);
    }
  },

  explorer(g, civ) {
    const k = person(g, civ, {
      helmet: 'brim',
      coat: true,
      legs: 0x8a7a5c,
      boots: COL.LEATHER,
      lHand: [1.4, 13.2, 0.6],
      lElbow: [1.4, 11.0, 2.0],
      rHand: [1.6, 8.2, -1.9],
    });
    // A spyglass to the eye, a pack with a bedroll, a rope at the belt.
    k.bar([1.4, 13.4, 0.5], [4.0, 13.8, 0.6], 0.22, 0.32, COL.BRONZE, 8);
    k.box(1.6, 3.4, 2.6, -1.9, 10.2, 0, COL.LEATHER);
    k.bar([-1.9, 12.2, -1.6], [-1.9, 12.2, 1.6], 0.6, 0.6, civ, 8);
    const coil = new T.Mesh(new T.TorusGeometry(0.8, 0.18, 4, 12), mat(COL.ROPE));
    coil.position.set(0.4, 7.4, 1.7);
    g.add(coil);
    k.bar([1.6, 0, -1.9], [1.6, 14.0, -1.9], 0.15, 0.17, COL.WOOD, 6);
  },

  dragoon(g, civ) {
    const { rider: k } = mounted(g, civ, { saddleCloth: true, coat: 0x8a6a4a }, {
      helmet: 'plume',
      legs: COL.WHITE,
      boots: COL.BLACK,
      lHand: [2.2, 8.4, 1.0],
      rHand: [2.6, 12.8, -1.8],
      rElbow: [1.0, 10.6, -2.6],
    });
    // A sabre raised, a carbine slung across the back.
    k.box(0.22, 4.8, 0.1, 3.2, 15.2, -1.8, COL.STEEL, 0, 0, -0.35);
    k.box(1.0, 0.25, 0.4, 2.6, 12.9, -1.8, COL.BRONZE);
    k.bar([-1.6, 8.4, -1.4], [0.6, 14.0, 1.4], 0.12, 0.12, COL.WOOD, 5);
    k.box(0.12, 4.6, 0.35, -1.25, 9.4, 0, COL.WHITE, 0.6);
  },

  trebuchet(g, civ) {
    const k = kit(g);
    for (const z of [2.0, -2.0]) {
      k.box(12, 0.9, 0.9, 0, 0.45, z, COL.WOOD);
      k.bar([-3.4, 0.9, z], [0, 12.6, z * 0.6], 0.36, 0.36, COL.WOOD, 4);
      k.bar([3.4, 0.9, z], [0, 12.6, z * 0.6], 0.36, 0.36, COL.WOOD, 4);
      k.bar([-1.6, 6.0, z * 0.8], [1.6, 6.0, z * 0.8], 0.2, 0.2, COL.WOODD, 4);
      k.box(4, 0.4, 0.05, -1.6, 0.45, z * 1.24, civ);
    }
    for (const x of [-5.4, 0, 5.4]) k.box(0.8, 0.8, 4.8, x, 0.45, 0, COL.WOODD);
    k.bar([0, 12.6, -1.6], [0, 12.6, 1.6], 0.4, 0.4, COL.IRON, 6);
    // The long arm cocked low at the back, counterweight box raised at the front.
    k.bar([3.8, 15.2, 0], [-10.4, 4.6, 0], 0.34, 0.24, COL.WOOD, 5);
    k.box(3.0, 3.0, 2.8, 4.6, 13.4, 0, COL.WOODD);
    for (const y of [12.4, 14.4]) k.box(3.1, 0.2, 2.9, 4.6, y, 0, COL.IRON);
    k.bar([-10.4, 4.6, 0], [-11.6, 0.8, 0], 0.05, 0.05, COL.ROPE, 3);
    k.ball(0.7, -11.6, 0.8, 0, 0x8f8a80, 0);
    k.bar([3.4, 1.0, 0], [3.4, 1.0, 0], 0.5, 0.5, COL.ROPE, 6);
    k.bar([-7.0, 0.9, 1.6], [-7.0, 9.0, 1.6], 0.08, 0.08, COL.WOODD, 4);
    k.shape([[0, 0], [2.4, -0.4], [0, -1.4]], 0, civ, -7.0, 8.9, 1.6);
  },

  galley(g, civ) {
    const k = kit(g);
    hull(k, 18, 4.0, 3.4, COL.WOOD, 2.6, 2.0);
    // Bronze ram, a painted eye, oars, one mast with a striped sail.
    k.cone([6.4, 1.0, 0], [9.6, 1.0, 0], 0.7, COL.BRONZE, 6);
    for (const z of [2.03, -2.03]) {
      k.disc(0.6, 10, COL.WHITE, 6.6, 3.0, z, z > 0 ? 0 : Math.PI);
      k.disc(0.3, 8, COL.DARK, 6.6, 3.0, z * 1.01, z > 0 ? 0 : Math.PI);
      k.box(14, 0.5, 0.05, -0.6, 2.6, z, civ);
    }
    oars(k, -5.6, 5.0, 8, 4.0, 3.4);
    k.bar([0.6, 3.4, 0], [0.6, 15.0, 0], 0.2, 0.16, COL.WOODD, 6);
    squareSail(g, k, civ, 0.6, 14.4, 7.0, 7.2, true, false);
    for (const z of [1.2, -1.2]) k.bar([0.6, 14.6, 0], [-8.6, 5.4, z], 0.03, 0.03, COL.DARK, 3);
    k.box(1.6, 1.2, 3.0, -7.4, 6.0, 0, COL.WOODD);
  },

  quinquereme(g, civ) {
    const k = kit(g);
    hull(k, 22, 5.0, 4.6, 0x8a6a45, 2.8, 2.4);
    k.cone([8.4, 1.2, 0], [11.8, 1.2, 0], 0.9, COL.BRONZE, 6);
    // Two banks of oars, a fighting tower, round shields hung along the rail.
    oars(k, -7.4, 6.4, 10, 5.0, 4.6, 3.6);
    oars(k, -7.0, 6.8, 10, 5.0, 4.6, 2.4);
    for (let x = -7; x <= 7; x += 2) for (const z of [2.6, -2.6]) k.disc(0.75, 10, civ, x, 5.2, z, z > 0 ? 0 : Math.PI);
    k.box(3.2, 3.2, 3.2, 4.4, 6.2, 0, COL.WOODD);
    for (const x of [3.0, 4.4, 5.8]) for (const z of [1.4, -1.4]) k.box(0.6, 0.8, 0.6, x, 8.2, z, COL.WOODD);
    k.bar([-0.8, 4.6, 0], [-0.8, 18.0, 0], 0.24, 0.2, COL.WOODD, 6);
    squareSail(g, k, civ, -0.8, 17.4, 8.4, 8.4, true, false);
    k.box(2.4, 2.0, 4.0, -9.4, 6.4, 0, COL.WOODD);
  },

  galleon(g, civ) {
    const k = kit(g);
    hull(k, 20, 5.2, 5.0, 0x7d5d3c, 2.4, 1.0);
    // High castles fore and aft, gunports, three masts of square sails with crosses.
    k.box(5.0, 3.2, 5.0, -7.0, 6.6, 0, 0x7d5d3c);
    k.box(5.2, 0.35, 5.2, -7.0, 8.3, 0, COL.WOODD);
    k.box(3.4, 2.0, 4.6, 7.0, 6.0, 0, 0x7d5d3c);
    for (const z of [2.62, -2.62]) {
      for (const x of [-4, -1.4, 1.2, 3.8]) k.box(0.7, 0.7, 0.05, x, 3.4, z, COL.DARK);
      k.box(17, 0.45, 0.06, -0.6, 4.5, z, civ);
      for (const x of [-8.4, -7, -5.6]) k.box(0.6, 0.9, 0.05, x, 7.0, z, 0xe6d6a8);
    }
    for (const [x, h, w] of [[4.6, 17, 6.4], [-0.6, 21, 7.4], [-6.0, 16, 5.4]] as Array<[number, number, number]>) {
      k.bar([x, 5.0, 0], [x, h, 0], 0.24, 0.18, COL.WOODD, 6);
      squareSail(g, k, civ, x, h - 0.8, w, 5.4, false, true);
      if (x !== -6.0) squareSail(g, k, civ, x, h - 7.0, w * 1.1, 5.0, false, false);
    }
    k.bar([10.6, 7.0, 0], [14.6, 9.6, 0], 0.18, 0.12, COL.WOODD, 5);
    for (const z of [2.2, -2.2]) {
      k.bar([-0.6, 20.6, 0], [-8.6, 8.4, z], 0.03, 0.03, COL.DARK, 3);
      k.bar([-0.6, 20.6, 0], [6.0, 6.0, z], 0.03, 0.03, COL.DARK, 3);
    }
    k.bar([-0.6, 21, 0], [-0.6, 22.6, 0], 0.06, 0.06, COL.WOODD, 4);
    k.shape([[0, 0], [3.0, -0.6], [0, -1.6]], 0, civ, -0.6, 22.5, 0);
  },

  shotelai(g, civ) {
    const k = person(g, civ, {
      helmet: 'hair',
      skin: DARK_SKIN,
      tunic: COL.WHITE,
      legs: DARK_SKIN,
      sleeve: COL.WHITE,
      boots: 0x9a7a52,
      lHand: [2.2, 9.6, 2.0],
      rHand: [1.6, 14.0, -1.8],
      rElbow: [0.4, 12.4, -2.6],
    });
    // A robe edged in the owner's colour, a round hide shield, and the hooked shotel.
    k.box(0.15, 5.6, 0.7, 1.2, 9.0, 0.5, civ, 0.3);
    k.bar([0, 5.6, 0], [0, 5.9, 0], 1.84, 1.84, civ, 10).scale.z = 0.86;
    const shield = new T.Mesh(new T.CylinderGeometry(2.6, 2.6, 0.4, 16), [mat(0xc4a77a), mat(civ), mat(0xc4a77a)]);
    shield.rotation.x = Math.PI / 2;
    shield.position.set(2.3, 9.6, 2.5);
    shield.rotation.y = -0.3;
    g.add(shield);
    k.ball(0.55, 2.4, 9.6, 2.8, COL.BRONZE, 1);
    const blade: V3[] = [];
    for (let i = 0; i <= 8; i++) {
      const a = (i / 8) * Math.PI * 0.85;
      blade.push([1.8 + Math.sin(a) * 3.2, 14.2 + (1 - Math.cos(a)) * 2.6, -1.8]);
    }
    for (let i = 0; i < 8; i++) k.bar(blade[i]!, blade[i + 1]!, 0.12, 0.12, COL.STEEL, 4);
  },

  war_elephant(g, civ) {
    const k = kit(g);
    elephant(k, 0x9a9590);
    // A cloth and a howdah in the owner's colour, a mahout and an archer.
    k.box(9.6, 0.4, 7.6, 0, 14.4, 0, civ);
    for (const z of [3.6, -3.6]) k.box(8.6, 4.2, 0.2, 0, 12.4, z, civ);
    for (const z of [3.72, -3.72]) k.box(8.6, 0.4, 0.06, 0, 10.6, z, COL.BRONZE);
    k.box(5.4, 2.4, 4.6, -0.6, 15.8, 0, COL.WOODD);
    k.box(5.6, 0.3, 4.8, -0.6, 17.1, 0, COL.BRONZE);
    for (const [x, z] of [[-3.2, 2.2], [-3.2, -2.2], [2.0, 2.2], [2.0, -2.2]] as Array<[number, number]>) k.bar([x, 17, z], [x, 21, z], 0.1, 0.1, COL.WOODD, 4);
    const canopy = new T.Mesh(new T.ConeGeometry(4.2, 1.6, 4), [mat(civ)]);
    canopy.rotation.y = Math.PI / 4;
    canopy.position.set(-0.6, 21.8, 0);
    g.add(canopy);
    const mahout = new T.Group();
    mahout.position.set(5.6, 9.8, 0);
    mahout.scale.setScalar(0.62);
    g.add(mahout);
    person(mahout, civ, { seated: true, skirt: false, helmet: 'topknot', skin: 0x9a6a46, legs: 0x9a6a46, lHand: [2.6, 9.6, 0.8], rHand: [3.2, 12.0, -0.8] });
    const archer = new T.Group();
    archer.position.set(-0.6, 10.6, 0);
    archer.scale.setScalar(0.6);
    g.add(archer);
    const a = person(archer, civ, { skirt: false, helmet: 'conical', skin: 0x9a6a46, lHand: [3.0, 11.6, 0.6], rHand: [-0.4, 11.6, -1.2] });
    a.bar([3.4, 6.4, 0.6], [3.6, 16.8, 0.6], 0.14, 0.14, COL.WOODD, 5);
  },

  druzhina(g, civ) {
    const { rider: k } = mounted(g, civ, { saddleCloth: true, coat: 0x6e5038 }, {
      helmet: 'conical',
      legs: 0x9aa3a8,
      boots: 0x7a3a2a,
      sleeve: 0x9aa3a8,
      pauldron: 0x9aa3a8,
      lHand: [2.2, 8.6, 1.4],
      lElbow: [1.2, 9.4, 2.4],
      rHand: [1.4, 8.6, -1.9],
    });
    // Mail coat over the owner's colour, a teardrop shield and a lance with a pennant.
    k.bar([0, 6.8, 0], [0, 10.6, 0], 1.66, 1.5, 0x9aa3a8, 10).scale.z = 0.82;
    const s = new T.Shape();
    s.moveTo(-1.6, 1.8);
    s.quadraticCurveTo(0, 2.4, 1.6, 1.8);
    s.quadraticCurveTo(1.4, -2.2, 0, -4.4);
    s.quadraticCurveTo(-1.4, -2.2, -1.6, 1.8);
    const shield = new T.Mesh(new T.ExtrudeGeometry(s, { depth: 0.3, bevelEnabled: false }), [mat(civ), mat(COL.WOODD)]);
    shield.position.set(1.2, 9.6, 2.7);
    shield.rotation.y = -0.3;
    k.add(shield);
    k.ball(0.45, 1.25, 9.8, 3.05, COL.BRONZE, 1);
    k.bar([-4, 8.3, -1.95], [16, 10.2, -2.1], 0.14, 0.2, COL.WOOD, 6);
    k.cone([16, 10.2, -2.1], [17.8, 10.37, -2.12], 0.24, COL.STEEL, 6);
    k.shape([[0, 0], [2.6, 0.2], [1.6, -0.6], [2.4, -1.4], [0, -1.2]], 0, civ, 12.6, 9.9, -2.1);
  },
};
